// useCreditAnalystRecords - loads credit-analyst commission-recipient records
// via the `listCreditAnalystRecords` callable (includeArchived: true), matching
// the legacy Team page's eager load. Only fetches when `enabled` (the legacy
// canManageOperationalRoles gate: super_admin | administrator | founder).
// Exposes a `refresh` so archive/restore mutations can re-pull.

import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import { functions } from '../../../../lib/firebase';

export interface CreditAnalystRecord {
  analystId: string;
  displayName: string;
  email: string | null;
  status: 'active' | 'archived';
  hasLogin: boolean;
  notes: string | null;
}

export interface CreditAnalystRecordsResult {
  records: CreditAnalystRecord[];
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useCreditAnalystRecords(
  companyId: string | undefined,
  enabled: boolean,
): CreditAnalystRecordsResult {
  const [records, setRecords] = useState<CreditAnalystRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!companyId || !enabled) return;
    setLoading(true);
    try {
      const fn = httpsCallable<
        { companyId: string; includeArchived?: boolean },
        { records: CreditAnalystRecord[] }
      >(functions, 'listCreditAnalystRecords');
      const result = await fn({ companyId, includeArchived: true });
      setRecords(result.data.records ?? []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('[TeamManagementV2] listCreditAnalystRecords failed:', err);
      toast.error('Failed to load credit analysts: ' + message);
    } finally {
      setLoading(false);
    }
  }, [companyId, enabled]);

  // Eager-load on mount so the tab badge reflects existing records immediately
  // (mirrors the legacy page's eager load; without it the badge reads 0 until
  // the tab is opened).
  useEffect(() => {
    if (companyId && enabled) {
      void refresh();
    }
  }, [companyId, enabled, refresh]);

  return { records, loading, refresh };
}
